import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { DisputeInfoResponse, OrderResponse } from '@afterpaytouch/portal-api'
import { DisputeInfoModalProvider, DisputeInfoStep, useDisputeInfoModalContext } from './DisputeInfoContext'
import { Heading, Modal } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { isDisputeClosed } from './utils'

import { Detail } from './Detail'
import { SubmitEvidence } from './SubmitEvidence'

export interface DisputeInfoModalProps {
  disputeInfo: DisputeInfoResponse
  order: OrderResponse
  isModalOpen: boolean
  closeModal: () => void
  refresh: () => void
}

const I18N_NAMESPACE = ['dispute']

const Step: FC<{ isModalOpen: boolean }> = ({ isModalOpen }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { step, resetState, closeModal, disputeInfo, isLoading, isSubmitEvidence } = useDisputeInfoModalContext()

  const title = useMemo(() => {
    if (step === DisputeInfoStep.SubmitEvidence) {
      return isSubmitEvidence ? t('dispute:info:title:submitEvidence') : t('dispute:info:title:reject')
    }

    return t(`dispute:info:title:${isDisputeClosed(disputeInfo) ? 'closed' : 'normal'}`)
  }, [step, t, disputeInfo, isSubmitEvidence])

  const renderStep = useCallback(() => {
    switch (step) {
      case DisputeInfoStep.Detail:
        return <Detail />
      case DisputeInfoStep.SubmitEvidence:
        return <SubmitEvidence />
    }
  }, [step])

  useEffect(() => {
    return resetState
  }, [])

  return (
    <Modal show={isModalOpen} onClose={closeModal} onBackdropClick={closeModal} onHide={closeModal} loading={isLoading}>
      <Modal.Header divider={true}>
        <div className='text-center'>
          <Heading size='L'>{title}</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>{renderStep()}</Modal.Content>
    </Modal>
  )
}

export const DisputeInfoModal: FC<DisputeInfoModalProps> = ({ disputeInfo, isModalOpen, closeModal, order, refresh }) => {
  return isModalOpen ? (
    <DisputeInfoModalProvider disputeInfo={disputeInfo} closeModal={closeModal} order={order} refresh={refresh}>
      <Step isModalOpen={isModalOpen} />
    </DisputeInfoModalProvider>
  ) : null
}
